import __request__ from 'routing/request';
interface EventProcedure {
    readonly description: string;
    readonly _scope: string;
    readonly id: string;
    readonly title: string;
    readonly steps: EventProcedureStep[];
}
interface EventProcedureResponse__AssociatedEntities {
    readonly entity_count: number;
    readonly anomaly_detectors: EventProcedureResponse__AssociatedEntities__Entity[];
    readonly sigma_rules: EventProcedureResponse__AssociatedEntities__Entity[];
    readonly event_definitions: EventProcedureResponse__AssociatedEntities__Entity[];
}
interface ActionConfig {
    readonly type: string;
}
interface PaginatedResponse_EventProcedureStep {
    readonly [_key: string]: Object;
}
interface PaginatedResponse_EventProcedureResponse {
    readonly [_key: string]: Object;
}
interface ActionDto {
    readonly config: ActionConfig;
}
interface EventProcedureResponse__AssociatedEntities__Entity {
    readonly id: string;
    readonly title: string;
}
interface EventProcedureResponse {
    readonly associated_entities: EventProcedureResponse__AssociatedEntities;
    readonly description: string;
    readonly _scope: string;
    readonly id: string;
    readonly title: string;
    readonly steps: EventProcedureStep[];
}
interface EntityShareRequest {
    readonly selected_grantee_capabilities: {
        readonly [_key: string]: 'view' | 'manage' | 'own';
    };
    readonly selected_collections: string[];
}
interface EventProcedureStep {
    readonly action: ActionDto;
    readonly description: string;
    readonly _scope: string;
    readonly id: string;
    readonly title: string;
}
interface CreateEntityRequest_EventProcedureStep {
    readonly entity: EventProcedureStep;
    readonly share_request: EntityShareRequest;
}
interface CreateEntityRequest_EventProcedure {
    readonly entity: EventProcedure;
    readonly share_request: EntityShareRequest;
}
/**
 * Get a paginated list of event procedures
 * @param sort The field to sort the result on
 * @param direction The sort direction
 */
export function listProcedures(sort: 'title' | 'description' = 'title', page: number = 1, per_page: number = 15, query?: string, filters?: string, direction: 'asc' | 'desc' = 'asc'): Promise<PaginatedResponse_EventProcedureResponse> {
    return __request__('GET', '/plugins/org.graylog.plugins.securityapp/event_procedures', null, { 'page': page, 'per_page': per_page, 'query': query, 'filters': filters, 'sort': sort, 'direction': direction }, {
        'Accept': ['application/json']
    });
}
/**
 * Create a new event procedure
 */
export function createProcedure(JSONBody?: CreateEntityRequest_EventProcedure): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.securityapp/event_procedures', JSONBody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get procedures currently using a step
 */
export function getStepUsages(id: string): Promise<unknown> {
    return __request__('GET', `/plugins/org.graylog.plugins.securityapp/event_procedures/procedures_with_step/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get a paginated list of event procedure steps
 * @param sort The field to sort the result on
 * @param direction The sort direction
 */
export function listSteps(sort: 'title' | 'description' = 'title', page: number = 1, per_page: number = 15, query?: string, filters?: string, direction: 'asc' | 'desc' = 'asc'): Promise<PaginatedResponse_EventProcedureStep> {
    return __request__('GET', '/plugins/org.graylog.plugins.securityapp/event_procedures/steps', null, { 'page': page, 'per_page': per_page, 'query': query, 'filters': filters, 'sort': sort, 'direction': direction }, {
        'Accept': ['application/json']
    });
}
/**
 * Create a new event procedure step
 */
export function createStep(JSONBody?: CreateEntityRequest_EventProcedureStep): Promise<unknown> {
    return __request__('POST', '/plugins/org.graylog.plugins.securityapp/event_procedures/steps', JSONBody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Edit an existing event procedure step
 */
export function editStep(id: string, JSONBody?: EventProcedureStep): Promise<unknown> {
    return __request__('PUT', `/plugins/org.graylog.plugins.securityapp/event_procedures/steps/${id}`, JSONBody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete an existing procedure step
 */
export function deleteStep(id: string): Promise<unknown> {
    return __request__('DELETE', `/plugins/org.graylog.plugins.securityapp/event_procedures/steps/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an existing event procedure step
 */
export function getStep(id: string): Promise<unknown> {
    return __request__('GET', `/plugins/org.graylog.plugins.securityapp/event_procedures/steps/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get parameters for a view used in an event procedure action
 */
export function getViewParameters(id: string, type: string): Promise<unknown> {
    return __request__('GET', `/plugins/org.graylog.plugins.securityapp/event_procedures/view_parameters/${id}/${type}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an existing event procedure
 */
export function getProcedure(id: string): Promise<EventProcedureResponse> {
    return __request__('GET', `/plugins/org.graylog.plugins.securityapp/event_procedures/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Edit an existing event procedure
 */
export function editProcedure(id: string, JSONBody?: EventProcedure): Promise<unknown> {
    return __request__('PUT', `/plugins/org.graylog.plugins.securityapp/event_procedures/${id}`, JSONBody, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Delete an existing procedure
 */
export function deleteProcedure(id: string): Promise<unknown> {
    return __request__('DELETE', `/plugins/org.graylog.plugins.securityapp/event_procedures/${id}`, null, {}, {
        'Accept': ['application/json']
    });
}
/**
 * Get an existing event procedure decorated for a specific event
 */
export function getProcedureForEvent(id: string, eventId: string): Promise<EventProcedureResponse> {
    return __request__('GET', `/plugins/org.graylog.plugins.securityapp/event_procedures/${id}/${eventId}`, null, {}, {
        'Accept': ['application/json']
    });
}
